import {
  ICreateBrandRequest,
  ICreateOrganisationRequest,
} from "@entities/organisation/lib/types";
import { IBrand, IGetOrganisationPermissions } from "@shared/lib/types";
import { Avatar, Card, Drawer, Flex, Modal, Space, Typography } from "antd";
import { Briefcase, Building, Pen, UploadCloud } from "lucide-react";

import { ApartmentOutlined, PlusOutlined } from "@ant-design/icons";
import { useEditBrand } from "@entities/organisation/lib/hooks";
import { useCreateBrand } from "@entities/organisation/lib/hooks/useCreateBrand";
import { useEditOrganisation } from "@entities/organisation/lib/hooks/useEditOrganisation";
import { ManageBrand } from "@features/ManageBrand/ManageBrand";
import { ToastService } from "@shared/lib/services";
import { IBrandOrganisation } from "@shared/lib/types/brandOrganisation";
import { Button } from "@shared/ui/Button";
import { useEffect, useState } from "react";
import { OrganisationForm } from ".";
import { BILLING_TYPE } from "../lib/constants";
import BillingInfo from "./BillingInfo";
import { BrandForm } from "./BrandForm";
import styles from "./styles.module.scss";

export const SettingsTab = ({
  organisation,
  refetchOrganisationById,
  organisationId,
}: {
  organisation: IBrandOrganisation | undefined;
  refetchOrganisationById: () => void;
  organisationId: string;
}) => {
  const [modal, setModal] = useState<{
    organisation: IBrandOrganisation | null;
    open: boolean;
    userPermissions: IGetOrganisationPermissions | null;
    isEdit: boolean;
    brand: IBrand | null;
  }>({
    organisation: null,
    brand: null,
    open: false,
    userPermissions: null,
    isEdit: false,
  });
  const [openEditModal, setOpenEditModal] = useState<{
    open: boolean;
    organisation: IBrandOrganisation | null;
  }>({
    open: false,
    organisation: null,
  });

  // const { organisationBillingDetails, refetchOrganisationBillingDetails } =
  //   useGetBrandOrganisationBilling(organisationId);

  const { edit, isEdited, isEditing, editOrganisationError } =
    useEditOrganisation();

  const { create, isCreating, isCreated } = useCreateBrand(
    refetchOrganisationById
  );
  const { isUpdated, isUpdating, update } = useEditBrand(
    undefined,
    refetchOrganisationById
  );

  const [openManageBrand, setOpenManageBrand] = useState<{
    open: boolean;
    brandId: string;
  }>({
    open: false,
    brandId: "",
  });

  const [editingBrandId, setEditingBrandId] = useState<string | undefined>();

  useEffect(() => {
    if (isCreated) {
      ToastService.success("Brand created successfully");
    } else if (isUpdated) {
      ToastService.success("Brand updated successfully");
    }
    setModal({
      open: false,
      userPermissions: null,
      organisation: null,
      isEdit: false,
      brand: null,
    });
    setEditingBrandId(undefined);
  }, [isCreated, isUpdated]);

  useEffect(() => {
    if (isEdited) {
      ToastService.success("Company updated successfully");
      setOpenEditModal({
        open: false,
        organisation: null,
      });
    } else if (editOrganisationError) {
      ToastService.error(editOrganisationError.message);
    }
  }, [isEdited, editOrganisationError]);

  const handleSubmit = (brandData: ICreateBrandRequest) => {
    if (modal.isEdit) {
      update({ brand: brandData, brandId: modal.brand?._id || "" });
    } else {
      create({ ...brandData, brandOrganisationId: organisationId });
    }
  };

  const handleEditOrganisation = (organisation: ICreateOrganisationRequest) => {
    edit({ ...organisation, brandOrganisationId: organisationId });
  };

  const getEditModalTitle = () => {
    const prefix = modal.isEdit ? "Edit" : "Add";
    let postfix = "";
    if (!modal.userPermissions) {
      postfix = "Brand";
    } else if (modal.userPermissions?.userType === "brand-user") {
      postfix = "Employee";
    } else {
      postfix = "Member";
    }
    return `${prefix} ${postfix}`;
  };
  // const isBillingEmpty = organisation?.billingMode === BILLING_TYPE.Single?!Boolean(organisation?.brandBillingDetailsForSingleMode) : true;
  return (
    <div className={styles.tabPane}>
      <Space direction="vertical" style={{ width: "100%" }} size={24}>
        <Card
          title={
            <Flex align="center" gap={12}>
              <Avatar
                shape="square"
                size={32}
                icon={<Building size={18} />}
                className={styles.cardIconAvatar}
              />
              <Typography.Title level={5} className={styles.cardTitle}>
                Company Info
              </Typography.Title>
            </Flex>
          }
          extra={
            <Button
              type="link"
              icon={<Pen size={12} />}
              onClick={() => {
                setOpenEditModal({
                  open: true,
                  organisation: organisation || null,
                });
              }}
              className={styles.editButton}
            >
              Edit
            </Button>
          }
          className={styles.card}
        >
          <Flex align="center" gap={24}>
            <Avatar
              shape="square"
              size={80}
              src={organisation?.logoUrl}
              className={styles.orgAvatar}
            >
              {organisation?.name?.charAt(0)}
            </Avatar>
            <Flex vertical>
              <Typography.Title level={4} className={styles.cardTitle}>
                {organisation?.name}
              </Typography.Title>
              <Typography.Text type="secondary">Organisation</Typography.Text>
            </Flex>
          </Flex>
        </Card>

        <Card
          title={
            <Flex align="center" gap={12}>
              <Avatar
                shape="square"
                size={32}
                icon={<Briefcase size={18} />}
                className={styles.cardIconAvatar}
              />
              <Typography.Title level={5} className={styles.cardTitle}>
                Brands
              </Typography.Title>
            </Flex>
          }
          extra={
            <Button
              size="small"
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setModal({
                  open: true,
                  userPermissions: null,
                  organisation: organisation || null,
                  isEdit: false,
                  brand: null,
                });
              }}
            >
              Add Brand
            </Button>
          }
          className={styles.card}
        >
          <Space direction="vertical" size={12} style={{ width: "100%" }}>
            {organisation?.brands?.map((brand) => (
              <Card
                key={brand._id}
                className={styles.innerCard}
                onClick={() =>
                  setOpenManageBrand({
                    brandId: brand._id,
                    open: true,
                  })
                }
              >
                <Flex justify="space-between" align="center">
                  <Flex align="center" gap={16}>
                    <Avatar
                      shape="square"
                      size={40}
                      src={brand.logoUrl}
                      className={styles.brandAvatar}
                    >
                      {brand.name.charAt(0)}
                    </Avatar>
                    <Flex vertical>
                      <Typography.Text strong>{brand.name}</Typography.Text>
                      <Typography.Text
                        type="secondary"
                        className={styles.secondaryText}
                      >
                        <UploadCloud
                          size={10}
                          className={styles.secondaryTextIcon}
                        />
                        {brand.websiteUrl}
                      </Typography.Text>
                    </Flex>
                  </Flex>
                  <Button
                    type="text"
                    icon={<Pen size={14} />}
                    onClick={(e) => {
                      e.stopPropagation();
                      setModal({
                        open: true,
                        userPermissions: null,
                        organisation: organisation || null,
                        isEdit: true,
                        brand: brand,
                      });
                    }}
                  />
                </Flex>
              </Card>
            ))}
            {!organisation?.brands?.length && (
              <div className={styles.noData}>
                <Typography.Text type="secondary">
                  No brands added yet.
                </Typography.Text>
              </div>
            )}
          </Space>
        </Card>

        {/* Billing */}
        {organisation?.billingMode === BILLING_TYPE.Single ||
        !organisation?.isBillingEnabled ? (
          <BillingInfo
            billingDetails={organisation?.brandBillingDetailsForSingleMode}
            billingMode={organisation?.billingMode}
            brandId={editingBrandId}
            brandOrganisationId={organisationId}
            refetch={() => refetchOrganisationById()}
          />
        ) : null}
      </Space>
      <Modal
        open={modal.open}
        title={getEditModalTitle()}
        destroyOnClose
        onCancel={() =>
          setModal({
            open: false,
            userPermissions: null,
            organisation: null,
            isEdit: false,
            brand: null,
          })
        }
        footer={null}
      >
        <BrandForm
          onSubmit={(values: ICreateBrandRequest) => {
            handleSubmit(values);
          }}
          isSubmitting={isCreating || isUpdating}
          isEdit={modal.isEdit}
          initialValues={modal.brand || undefined}
        />
      </Modal>

      <Modal
        title={
          <div className={styles.formHeader}>
            <div className={styles.headerContent}>
              <div className={styles.iconWrapper}>
                <ApartmentOutlined />
              </div>
              <h2 className={styles.title}>Edit Company</h2>
            </div>
          </div>
        }
        open={openEditModal.open}
        onCancel={() => {
          setOpenEditModal({
            open: false,
            organisation: null,
          });
        }}
        centered
        footer={null}
        destroyOnClose
        styles={{
          body: {
            padding: 0,
          },
          content: {
            padding: 0,
          },
          header: {
            margin: 0,
          },
        }}
      >
        <OrganisationForm
          onSubmit={handleEditOrganisation}
          isSubmitting={isEditing}
          initialValues={openEditModal.organisation || {}}
        />
      </Modal>

      {/* <BillingModal
        open={billingModalOpen}
        onClose={() => {
          setBillingModalOpen(false);
          setEditingBrandId(undefined);
        }}
        initialData={
          billingModalMode === "edit"
            ? organisationBillingDetails?.data?.brandBillingDetailsForSingleMode
            : billingModalMode === "editBrandBilling"
              ? organisationBillingDetails?.data?.brands?.find(
                  (brand) => brand._id === editingBrandId
                )?.billing
              : undefined
        }
        mode={billingModalMode}
        organisationId={organisationId}
        brandId={editingBrandId}
      /> */}

      <Drawer
        open={openManageBrand.open}
        onClose={() => setOpenManageBrand({ open: false, brandId: "" })}
        width={800}
        title="Brand Details"
        destroyOnClose
      >
        <ManageBrand
          brandId={openManageBrand.brandId}
          paymentTerms={[]}
          brandOrganisationId={organisationId}
        />
      </Drawer>
    </div>
  );
};
